import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/App';
import { User, MapPin, Star, Package, Edit, Save, X, Phone, Mail, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ProfilePage() {
  const { userId } = useParams();
  const { user: currentUser, fetchCurrentUser } = useAuth();
  const [user, setUser] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editData, setEditData] = useState({
    name: '',
    phone_number: '',
    country_code: '+91',
    city: '',
    state: '',
    pin_code: '',
    country: ''
  });
  const [saving, setSaving] = useState(false);

  const isOwnProfile = currentUser && currentUser.id === userId;

  useEffect(() => {
    fetchProfile();
    fetchReviews();
  }, [userId]);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/users/${userId}`);
      setUser(response.data);
      setEditData({
        name: response.data.name || '',
        phone_number: response.data.phone_number || '',
        country_code: response.data.country_code || '+91',
        city: response.data.city || '',
        state: response.data.state || '',
        pin_code: response.data.pin_code || '',
        country: response.data.country || ''
      });
    } catch (error) {
      console.error('Failed to fetch profile', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/reviews/user/${userId}`);
      setReviews(response.data);
    } catch (error) {
      console.error('Failed to fetch reviews', error);
    }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/users/me`, editData);
      toast.success('Profile updated successfully!');
      setShowEditModal(false);
      fetchProfile();
      // Refresh user in context
      fetchCurrentUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>User not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-slate-600 hover:text-indigo-600 mb-6 transition-colors"
          data-testid="back-to-home"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="font-medium">Back to Home</span>
        </Link>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-lg overflow-hidden">
          {/* Profile Header */}
          <div className="bg-gradient-to-br from-indigo-600 to-violet-600 px-8 py-12">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-6">
                <div className="w-24 h-24 bg-white rounded-full flex items-center justify-center">
                  <User className="w-12 h-12 text-indigo-600" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {user.name}
                  </h1>
                  <div className="flex items-center space-x-2 text-indigo-100 mb-2">
                    <Mail className="w-4 h-4" />
                    <span>{user.email}</span>
                  </div>
                  {user.phone_number && (
                    <div className="flex items-center space-x-2 text-indigo-100 mb-2">
                      <Phone className="w-4 h-4" />
                      <span>{user.country_code} {user.phone_number}</span>
                    </div>
                  )}
                  {(user.city || user.state || user.country) && (
                    <div className="flex items-center space-x-2 text-indigo-100">
                      <MapPin className="w-4 h-4" />
                      <span>
                        {[user.city, user.state, user.country].filter(Boolean).join(', ')}
                      </span>
                    </div>
                  )}
                  {user.pin_code && (
                    <div className="text-indigo-200 text-sm mt-1">
                      Pin Code: {user.pin_code}
                    </div>
                  )}
                  <div className="flex items-center space-x-4 mt-3">
                    <div className="flex items-center space-x-1">
                      <Star className="w-5 h-5 text-amber-400 fill-amber-400" />
                      <span className="text-white font-semibold">{user.rating.toFixed(1)}</span>
                    </div>
                    <span className="text-indigo-200">{user.reviews_count} reviews</span>
                    {user.verified && (
                      <span className="px-3 py-1 bg-lime-500 text-slate-900 rounded-full text-sm font-semibold">
                        Verified
                      </span>
                    )}
                  </div>
                </div>
              </div>
              
              {isOwnProfile && (
                <button
                  onClick={() => setShowEditModal(true)}
                  className="flex items-center gap-2 bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-full transition-colors"
                  data-testid="edit-profile-btn"
                >
                  <Edit className="w-4 h-4" />
                  Edit Profile
                </button>
              )}
            </div>
          </div>

          {/* Profile Stats */}
          <div className="grid grid-cols-3 gap-4 px-8 py-6 border-b border-slate-200">
            <div className="text-center">
              <p className="text-2xl font-bold text-slate-900">{user.reviews_count}</p>
              <p className="text-sm text-slate-600">Reviews</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-slate-900">{user.rating.toFixed(1)}</p>
              <p className="text-sm text-slate-600">Rating</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-slate-900">
                {new Date(user.created_at).toLocaleDateString()}
              </p>
              <p className="text-sm text-slate-600">Member Since</p>
            </div>
          </div>

          {/* Reviews Section */}
          <div className="px-8 py-6">
            <h2 className="text-2xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Reviews
            </h2>
            {reviews.length === 0 ? (
              <div className="text-center py-12">
                <Package className="w-16 h-16 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-600">No reviews yet</p>
              </div>
            ) : (
              <div className="space-y-4">
                {reviews.map((review) => (
                  <div key={review.id} className="bg-slate-50 rounded-xl p-4" data-testid={`review-${review.id}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-1">
                        {[...Array(5)].map((_, i) => (
                          <Star
                            key={i}
                            className={`w-4 h-4 ${
                              i < review.rating
                                ? 'text-amber-400 fill-amber-400'
                                : 'text-slate-300'
                            }`}
                          />
                        ))}
                      </div>
                      <span className="text-xs text-slate-500">
                        {new Date(review.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    {review.comment && <p className="text-slate-700">{review.comment}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Edit Profile Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-lg" data-testid="edit-profile-modal">
          <DialogHeader>
            <DialogTitle>Edit Profile</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Name</label>
              <input
                type="text"
                value={editData.name}
                onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                className="w-full rounded-xl border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-500 h-12 px-4"
                placeholder="Your name"
                data-testid="edit-name-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Phone Number</label>
              <div className="flex gap-2">
                <select
                  value={editData.country_code}
                  onChange={(e) => setEditData({ ...editData, country_code: e.target.value })}
                  className="w-24 rounded-xl border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-500 h-12 px-2"
                  data-testid="edit-country-code"
                >
                  <option value="+91">+91</option>
                  <option value="+1">+1</option>
                  <option value="+44">+44</option>
                  <option value="+61">+61</option>
                  <option value="+971">+971</option>
                </select>
                <input
                  type="tel"
                  value={editData.phone_number}
                  onChange={(e) => setEditData({ ...editData, phone_number: e.target.value })}
                  className="flex-1 rounded-xl border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-500 h-12 px-4"
                  placeholder="Phone number"
                  data-testid="edit-phone-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Country</label>
                <input
                  type="text"
                  value={editData.country}
                  onChange={(e) => setEditData({ ...editData, country: e.target.value })}
                  className="w-full rounded-xl border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-500 h-12 px-4"
                  placeholder="Country"
                  data-testid="edit-country-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">State</label>
                <input
                  type="text"
                  value={editData.state}
                  onChange={(e) => setEditData({ ...editData, state: e.target.value })}
                  className="w-full rounded-xl border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-500 h-12 px-4"
                  placeholder="State"
                  data-testid="edit-state-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">City</label>
                <input
                  type="text"
                  value={editData.city}
                  onChange={(e) => setEditData({ ...editData, city: e.target.value })}
                  className="w-full rounded-xl border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-500 h-12 px-4"
                  placeholder="City"
                  data-testid="edit-city-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Pin Code</label>
                <input
                  type="text"
                  value={editData.pin_code}
                  onChange={(e) => setEditData({ ...editData, pin_code: e.target.value })}
                  className="w-full rounded-xl border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-500 h-12 px-4"
                  placeholder="Pin code"
                  data-testid="edit-pincode-input"
                />
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => setShowEditModal(false)}
                className="flex-1"
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveProfile}
                className="flex-1 bg-indigo-600 hover:bg-indigo-700"
                disabled={saving}
                data-testid="save-profile-btn"
              >
                {saving ? (
                  <span className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Saving...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Save className="w-4 h-4" />
                    Save Changes
                  </span>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
